<template>
  <Sidebar
    :visible.sync="localShowHide"
    :baseZIndex="100"
    position="right"
    class="p-sidebar-md tactic-filters-wrapper"
  >
    <div class="tactic-filters-header p-d-flex p-jc-between p-ai-center">
      <h2>Filters</h2>
      <div class="p-d-flex p-ai-center">
        <Button
          v-if="$store.getters.activeFiltersCount"
          label="Clear Filters"
          @click="onClearFiltersClick"
          :badge="$store.getters.activeFiltersCount"
          class="p-mr-2"
        />
        <Button
          icon="pi pi-times"
          class="p-button-rounded p-button-text p-button-sm"
          @click="localShowHide = false"
        />
      </div>
    </div>
    <div v-if="isInitiativesViewDisplayed" class="p-field-checkbox p-d-flex p-jc-between p-mb-4 p-mx-3">
      <label for="show_long_running_tactics">Show long-running tactics</label>
      <InputSwitch id="show_long_running_tactics" v-model="showLongRunningTacticsInInitiativesView" />
    </div>
    <Accordion
      :multiple="true"
      class="widgets-accordion"
      :activeIndex="accordionActiveIndicies"
      @tab-open="onAccordionTabOpen"
      @tab-close="onAccordionTabClose"
    >
      <AccordionTab header="Channels" :disabled="!currentChannelsSorted.length || isInitiativesViewDisplayed">
        <template #default>
          <div
            v-if="currentChannelsSorted.length >= 2"
            class="p-d-flex p-jc-end p-mb-3"
          >
            <Button
              :label="getChannelsToggleLabel()"
              class="p-button-text p-button-sm"
              @click="onToggleAllChannels"
            />
          </div>
          <Accordion
            :multiple="false"
            class="channels-accordion"
          >
            <AccordionTab
              v-for="(channel, index) of currentChannelsSorted"
              :key="channel.key"
              class="channel-accordion-tab"
            >
              <template #header>
                <div class="p-field-checkbox channel-checkbox">
                  <Checkbox
                    :id="'channel_' + index"
                    :value="channel"
                    :name="channel.name"
                    v-model="checkedChannels"
                    @change="onChannelCheckboxChange($event, channel)"
                  />
                  <label
                    :for="'channel_' + index"
                    :class="{
                      'is-lead': channel.isLead,
                      'is-nested': channel.isNested,
                    }"
                  >
                    <template v-if="isLeadPlan && channel.isNested">
                      {{ channel.abbreviatedPlanName }}  |  {{ channel.name }}
                    </template>
                    <template v-else>
                      {{ channel.name }}
                    </template>
                  </label>
                  <div class="channel-selected-types-count" v-if="getSelectedTypesForChannel(channel).length">
                    {{ getSelectedTypesForChannel(channel).length }} / {{ channel.tacticTypes.length }}
                  </div>
                </div>
              </template>
              <template #default>
                <template v-for="(type, tIndex) of channel.tacticTypes">
                  <div
                    v-if="type.name.toLowerCase() !== $store.getters.monthlyFocusTypeName || (type.name.toLowerCase() === $store.getters.monthlyFocusTypeName && channel.useMonthlyFocusType)"
                    :key="type.key"
                    class="p-field-checkbox type-checkbox"
                  >
                    <Checkbox
                      :id="'channel_' + index + '_type_' + tIndex"
                      :value="type"
                      :name="type.name"
                      v-model="selectedTacticTypes"
                      @change="onCheckboxChange"
                    />
                    <label :for="'channel_' + index + '_type_' + tIndex">
                      {{ type.name }}
                    </label>
                  </div>
                </template>
              </template>
            </AccordionTab>
          </Accordion>
        </template>
      </AccordionTab>
      <AccordionTab
        header="Initiatives"
        :disabled="!$store.getters.currentInitiatives.length && !filterNoInitiative"
      >
        <template #default>
          <Accordion
            :multiple="false"
            class="initiative-types-accordion"
          >
            <AccordionTab
              v-for="(type, i) in categorizedInitiatives"
              :key="'category_' + i"
              class="initiative-type-accordion-tab"
            >
              <template #header>
                <div class="p-field-checkbox initiative-type-checkbox">
                  <Checkbox
                    :id="'initiative_type_' + i"
                    :value="type"
                    :name="type.name"
                    v-model="checkedInitiativeTypes"
                    @change="onInitiativeTypeCheckboxChange($event, type)"
                  />
                  <label :for="'initiative_type_' + i">{{ type.name }}</label>
                  <div class="initiative-type-selected-count" v-if="getSelectedInitiativesCountForType(type)">
                    {{ getSelectedInitiativesCountForType(type) }} / {{ getVisibleInitiativesCountForType(type) }}
                  </div>
                </div>
              </template>
              <template #default>
                <Accordion
                  :multiple="true"
                  class="initiatives-accordion"
                >
                  <template v-for="(initiative, index) of type.initiatives">
                    <template v-if="isPlanningItemTouchingMonth(initiative)">
                      <AccordionTab
                        :key="initiative.key"
                        class="initiative-accordion-tab"
                      >
                        <template #header>
                          <div class="p-field-checkbox">
                            <Checkbox
                              :id="'initiative_' + type.id.intID + '_' + index"
                              :value="initiative"
                              :name="initiative.title"
                              v-model="selectedInitiatives"
                              @change="onCheckboxChange"
                            />
                            <label
                              :for="'initiative_' + type.id.intID + '_' + index"
                              :class="{
                                'is-nested': initiative.isNested,
                                'is-lead': initiative.isLead,
                              }"
                            >
                              <template v-if="isLeadPlan && initiative.isNested">
                                {{ initiative.abbreviatedPlanName }}  |  {{ initiative.title }}
                              </template>
                              <template v-else>
                                {{ initiative.title }}
                              </template>
                            </label>
                          </div>
                        </template>
                        <template #default>
                          <template v-for="(customization, index) of initiative.initiativeCustomizations">
                            <div
                              :key="customization.key"
                              class="p-field-checkbox nested-variant-item"
                              v-if="!isPlanningItemInFilteredOutPlan(customization) && (isPlanningItemTouchingMonth(customization) && (isLeadPlan || (!isLeadPlan && customization.planId.intID === currentPlan.id.intID)))"
                            >
                              <Checkbox
                                :id="'initiative_customization_' + type.id.intID + '_' + initiative.id.intID + '_' + index"
                                :value="customization"
                                :name="customization.title"
                                v-model="selectedInitiativeCustomizations"
                                @change="onCheckboxChange"
                              />
                              <label
                                :for="'initiative_customization_' + type.id.intID + '_' + initiative.id.intID + '_' + index"
                                class="is-nested-variant"
                              >
                                {{ customization.abbreviatedPlanName }}  |  {{ customization.title }}
                              </label>
                            </div>
                          </template>
                        </template>
                      </AccordionTab>
                    </template>
                  </template>
                </Accordion>
              </template>
            </AccordionTab>
          </Accordion>
          <div class="p-field-checkbox no-initiative-filter-row p-mt-5">
            <Checkbox
              id="filter_no_initiative"
              :binary="true"
              v-model="filterNoInitiative"
            />
            <label for="filter_no_initiative">No Initiatives linked</label>
          </div>
        </template>
      </AccordionTab>
      <AccordionTab
        header="Tags"
        :disabled="$store.getters.currentTags.length ? false : true"
      >
        <template #default>
          <div
            v-for="(tagCategory, tagCategoryIndex) in $store.getters.currentPlanTagCategories"
            :key="'tag_category_' + tagCategoryIndex"
          >
            <div :class="{'p-mt-5': tagCategoryIndex != 0}" class="p-mb-3 p-d-flex p-jc-between p-ai-center">
              <span>{{ tagCategory.name }}</span>
              <Button
                v-if="tagCategory.tags.length >= 2"
                :label="getTagCategoryToggleLabel(tagCategory)"
                class="p-button-text p-button-sm"
                @click="onToggleAllTagsInCategory(tagCategory)"
              />
            </div>
            <div
              v-for="(tag, tagIndex) of tagCategory.tags"
              :key="tag.key"
              >
              <div class="p-field-checkbox">
                <Checkbox
                  :id="'tag_' + tagCategoryIndex + '_' + tagIndex"
                  :value="tag"
                  :name="tag.title"
                  v-model="selectedTags"
                  @change="onCheckboxChange"
                />
                <label
                  :for="'tag_' + tagCategoryIndex + '_' + tagIndex"
                  :class="{
                    'is-lead': tag.isLead,
                    'is-nested': tag.isNested,
                  }"
                >
                  <template v-if="isLeadPlan && tag.isNested">
                    {{ tag.abbreviatedPlanName }}  |  {{ tag.title }}
                  </template>
                  <template v-else>
                    {{ tag.title }}
                  </template>
                </label>
              </div>
            </div>
          </div>
          <div class="p-field-checkbox no-tag-filter-row p-mt-5">
            <Checkbox
              id="filter_no_tag"
              :binary="true"
              v-model="filterNoTag"
            />
            <label for="filter_no_tag">No Tags</label>
          </div>
        </template>
      </AccordionTab>
      <AccordionTab
        header="Calendars of Interest"
        v-if="shouldShowCalendarsOfInterest"
        :disabled="!$store.getters.currentSubscriptions.length" 
      >
        <template #default>
          <div
            v-for="(sub, index) of $store.getters.currentSubscriptions"
            :key="sub.key"
            class="p-field-checkbox"
          >
            <Checkbox
              :id="'subscription_' + index"
              :value="sub"
              :name="sub.name"
              v-model="selectedSubscriptions"
            />
            <label :for="'subscription_' + index">{{ sub.name }}</label>
          </div>
        </template>
      </AccordionTab>
    </Accordion>
  </Sidebar>
</template>

<script lang="ts">
import Vue from 'vue'
import Checkbox from 'primevue/checkbox'
import Sidebar from 'primevue/sidebar'
import Inplace from 'primevue/inplace'
import InputText from 'primevue/inputtext'
import InputSwitch from 'primevue/inputswitch'
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import CustomView from '@/models/CustomView'
import Channel from '@/models/Channel'
import Tactic from '@/models/Tactic'
import Tag from '@/models/Tag'
import Subscription from '@/models/Subscription'
import Panel from 'primevue/panel'
import Accordion from 'primevue/accordion'
import AccordionTab from 'primevue/accordiontab'
import Plan from '@/models/Plan'
import TagCategory from '@/models/TagCategory'
import TacticType from '@/models/TacticType'
import InitiativeType from '@/models/InitiativeType'
import Initiative from '@/models/Initiative'
import PlanningItem from '@/models/InterfacePlanningItem'
import InitiativeCustomization from '@/models/InitiativeCustomization'

Vue.component('Checkbox', Checkbox)
Vue.component('Sidebar', Sidebar)
Vue.component('Inplace', Inplace)
Vue.component('InputText', InputText)
Vue.component('InputSwitch', InputSwitch)
Vue.component('Button', Button)
Vue.component('Panel', Panel)
Vue.component('Accordion', Accordion)
Vue.component('AccordionTab', AccordionTab)
Vue.component('Badge', Badge)

export default Vue.extend({
  name: 'TacticFilters',
  props: {
    showHide: Boolean,
  },
  data: () => {
    return {
      localSelectedTacticTypes: [] as TacticType[],
      localSelectedInitiatives: [] as Initiative[],
      localSelectedInitiativeCustomizations: [] as InitiativeCustomization[],
      localSelectedTags: [] as Tag[],
      localSelectedSubscriptions: [] as Subscription[],
      updateFiltersCountTimer: null as ReturnType<typeof setTimeout> | null,
      currentRoutePath: '' as string,
    }
  },
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    currentLeadPlan(): Plan {
      if (this.currentPlan.parentId.intID !== 0) {
        return this.$store.getters.allPlans.find(
          (plan) => plan.id.intID == this.currentPlan.parentId.intID
        )
      }
      return {} as Plan
    },
    currentNestedPlans(): Plan[] {
      return this.$store.getters.allPlans.filter((plan) =>
        this.currentPlan.plans.find((planId) => planId.intID == plan.id.intID)
          ? true
          : false
      )
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    isInitiativesViewDisplayed(): boolean {
      return this.currentRoutePath.endsWith('/initiatives')
    },
    currentLeadPlanFilterKey(): string {
      return 'lpl-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentNestedPlansFilterKey(): string {
      return 'npl-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentTacticTypesFilterKey(): string {
      return 'tp-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentInitiativesFilterKey(): string {
      return 'in-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentTagsFilterKey(): string {
      return 'tg-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentSubscriptionsFilterKey(): string {
      return 'sb-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentNoInitiativeFilterKey(): string {
      return 'ni-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentNoTagFilterKey(): string {
      return 'nt-' + this.$store.getters.currentPlanIntID.toString()
    },
    localShowHide: {
      get(): boolean {
        return this.showHide
      },
      set(value: boolean) {
        if (!value) {
          this.$emit('shouldHide')
        }
      },
    },
    currentTagsSorted(): Tag[] {
      const localCurrentTags = [...this.$store.getters.currentTags]
      return localCurrentTags.sort((a, b) => {
        const nameA = a.text.toUpperCase()
        const nameB = b.text.toUpperCase()
        if (nameA < nameB) {
          return -1
        }
        if (nameA > nameB) {
          return 1
        }
        return 0
      })
    },
    currentChannelsSorted(): Channel[] {
      const localCurrentChannels = [...this.$store.getters.currentChannels]
      return localCurrentChannels
        .filter((channel: Channel) => {
          // Filter out channels from deseletced lead/nested plans
          return channel.isNested ? this.visibleNestedPlanIntIds.includes(channel.planId.intID) : true
        })
        .sort((a, b) => {
          if (a.isLead && !b.isLead) {
            return -1
          }
          if (!a.isLead && b.isLead) {
            return 1
          }
          return 0
        })
        .sort((a, b) => {
          if (a.orderIndex === b.orderIndex) {
            return 0
          }
          return a.orderIndex > b.orderIndex ? 1 : -1
        })
    },
    isLeadPlanVisible: {
      get(): boolean {
        return this.$store.getters.isLeadPlanVisible
      },
      set(newValue: boolean) {
        this.$store.commit('updateIsLeadPlanVisible', newValue)
        window.localStorage.setItem(
          this.currentLeadPlanFilterKey,
          this.$store.getters.isLeadPlanVisibleQueryString
        )
      },
    },
    showLongRunningTacticsInInitiativesView: {
      get(): boolean {
        return this.$store.getters.showLongRunningTacticsInInitiativesView
      },
      set(newValue: boolean) {
        this.$store.commit('updateShowLongRunningTacticsInInitiativesView', newValue)
      },
    },
    visibleNestedPlanIntIds: {
      get(): number[] {
        return this.$store.getters.visibleNestedPlansIntIds
      },
      set(newValue: number[]) {
        if (newValue.length == 0) {
          this.$store.commit('updateVisibleNestedPlansIntIds', [])
          window.localStorage.setItem(
            this.currentNestedPlansFilterKey,
            ''
          )
          this.$store.commit('isNestedPlanFilterActive', false)
        } else {
          this.$store.commit('updateVisibleNestedPlansIntIds', newValue)
          window.localStorage.setItem(
            this.currentNestedPlansFilterKey,
            this.$store.getters.visibleNestedPlansQueryString
          )
          this.$store.commit('isNestedPlanFilterActive', true)
        }
      },
    },
    checkedChannels: {
      get(): Channel[] {
        // If no filter is active, no checkboxes are checked
        if (!this.localSelectedTacticTypes.length) {
          return []
        }
        // Otherwise, return channels where all tactic types are selected
        const selectedTypeIntIds = this.selectedTacticTypes.map((type: TacticType) => type.id.intID)
        return this.currentChannelsSorted.filter((channel: Channel) => {
          return channel.tacticTypes.every((type: TacticType) => selectedTypeIntIds.includes(type.id.intID))
        })
      },
      set(channels: Channel[]) {
        // do nothing
      }
    },
    checkedInitiativeTypes: {
      get(): InitiativeType[] {
        const selectedInitiativeIntIds = this.selectedInitiatives.map((init: Initiative) => init.id.intID)
        return this.categorizedInitiatives.filter((type: InitiativeType) => {
          const visibleInitiatives = type.initiatives.filter((init: Initiative) => this.isPlanningItemTouchingMonth(init))
          if (visibleInitiatives.length === 0) return false
          return visibleInitiatives.every((init: Initiative) => selectedInitiativeIntIds.includes(init.id.intID))
        })
      },
      set(types: InitiativeType[]) {
        // do nothing
      }
    },
    selectedTacticTypes: {
      get(): TacticType[] {
        if (this.localSelectedTacticTypes.length) {
          return this.$store.getters.selectedTacticTypes
        }
        return []
      },
      set(newValue: TacticType[]) {
        this.localSelectedTacticTypes = newValue
        if (newValue.length == 0) {
          this.$store.commit('updateSelectedTacticTypes', this.$store.getters.currentTacticTypes)
          window.localStorage.removeItem(this.currentTacticTypesFilterKey)
          this.$store.commit('isChannelFilterActive', false)
        } else {
          this.$store.commit('updateSelectedTacticTypes', newValue)
          window.localStorage.setItem(this.currentTacticTypesFilterKey, this.$store.getters.selectedTacticTypesQueryString)
          this.$store.commit('isChannelFilterActive', true)
        }
        this.manageSubscriptionFilters()
      },
    },
    selectedInitiatives: {
      get(): Initiative[] {
        if (this.localSelectedInitiatives.length) {
          return this.$store.getters.selectedInitiatives
        }
        return []
      },
      set(newValue: Initiative[]) {
        // If "no initiative" filter was active, clear it
        if (newValue.length > 0 && this.$store.getters.filterNoInitiative) {
          this.$store.commit('filterNoInitiative', false)
          window.localStorage.removeItem(this.currentNoInitiativeFilterKey)
        }
        // Store previous state before updating
        const previousInitiatives = [...this.localSelectedInitiatives]
        this.localSelectedInitiatives = newValue

        // Determine which initiatives were added and which were removed
        const addedInitiatives = newValue.filter(init => !previousInitiatives.includes(init))
        const removedInitiatives = previousInitiatives.filter(init => !newValue.includes(init))

        // Remove customizations for deselected initiatives
        removedInitiatives.forEach((initiative) => {
          initiative.initiativeCustomizations?.forEach((customization) => {
            this.selectedInitiativeCustomizations = this.selectedInitiativeCustomizations.filter(
              selected => selected.id.intID !== customization.id.intID
            )
          })
        })

        // Add customizations for newly selected initiatives
        addedInitiatives.forEach((initiative) => {
          initiative.initiativeCustomizations?.forEach((customization) => {
            if(this.isLeadPlan || customization.planId.intID === this.currentPlan.id.intID){
              // Only add if not already in the list
              if (!this.selectedInitiativeCustomizations.find(c => c.id.intID === customization.id.intID)) {
                this.selectedInitiativeCustomizations = [...this.selectedInitiativeCustomizations, customization]
              }
            }
          })
        })

        // Clear or set global selected initiatives
        if (newValue.length == 0 && this.localSelectedInitiativeCustomizations.length == 0) {
          this.$store.commit('updateSelectedInitiatives', this.$store.getters.currentInitiatives)
          this.$store.commit('updateSelectedInitiativeCustomizations', this.$store.getters.currentInitiativeCustomizations)
          window.localStorage.removeItem(this.currentInitiativesFilterKey)
          this.$store.commit('isInitiativeFilterActive', false)
        } else {
          this.$store.commit('updateSelectedInitiatives', this.localSelectedInitiatives)
          window.localStorage.setItem(this.currentInitiativesFilterKey, this.$store.getters.selectedInitiativesQueryString)
          this.$store.commit('isInitiativeFilterActive', true)

          if(this.localSelectedInitiativeCustomizations.length == 0){
            this.$store.commit('updateSelectedInitiativeCustomizations', [])
          }
        }
        this.manageSubscriptionFilters()
      },
    },
    selectedInitiativeCustomizations: {
      get(): InitiativeCustomization[] {
        if (this.localSelectedInitiativeCustomizations.length) {
          return this.$store.getters.selectedInitiativeCustomizations
        }
        return []
      },
      set(newValue: InitiativeCustomization[]) {
        // If "no initiative" filter was active, clear it
        if (newValue.length > 0 && this.$store.getters.filterNoInitiative) {
          this.$store.commit('filterNoInitiative', false)
          window.localStorage.removeItem(this.currentNoInitiativeFilterKey)
        }
        this.localSelectedInitiativeCustomizations = newValue

        // Clear or set global selected initiatives
        if (newValue.length == 0 && this.localSelectedInitiatives.length == 0) {
          this.$store.commit('updateSelectedInitiativeCustomizations', this.$store.getters.currentInitiativeCustomizations)
          this.$store.commit('updateSelectedInitiatives', this.$store.getters.currentInitiatives)
          this.$store.commit('isInitiativeFilterActive', false)
        } else {
          this.$store.commit('updateSelectedInitiativeCustomizations', this.localSelectedInitiativeCustomizations)
          this.$store.commit('isInitiativeFilterActive', true)

          if(this.localSelectedInitiatives.length == 0){
            this.$store.commit('updateSelectedInitiatives', [])
          }
        }
        this.manageSubscriptionFilters()
      }
    },
    filterNoInitiative: {
      get(): boolean {
        return this.$store.getters.filterNoInitiative
      },
      set(newValue: boolean) {
        this.$store.commit('filterNoInitiative', newValue)
        if (newValue) {
          this.localSelectedInitiatives = []
          this.localSelectedInitiativeCustomizations = []
          this.$store.commit('updateSelectedInitiatives', [])
          this.$store.commit('updateSelectedInitiativeCustomizations', [])
          this.$store.commit('isInitiativeFilterActive', true)
          window.localStorage.setItem(this.currentNoInitiativeFilterKey, 'true')
          window.localStorage.removeItem(this.currentInitiativesFilterKey)
        } else {
          window.localStorage.removeItem(this.currentNoInitiativeFilterKey)
          if (this.localSelectedInitiatives.length === 0 && this.localSelectedInitiativeCustomizations.length === 0) {
            this.$store.commit('isInitiativeFilterActive', false)
          }
        }
        this.manageSubscriptionFilters()
      },
    },
    categorizedInitiatives(): InitiativeType[] {
      return this.$store.getters.allInitiativesInCategories?.map((type: InitiativeType) => {
        const returnType = {...type} as InitiativeType
        // Filter out initiatives from filtered out lead/nested plans
        returnType.initiatives = type.initiatives.filter((initiative: Initiative) => {
          return initiative.isNested ? this.visibleNestedPlanIntIds.includes(initiative.planId.intID) : true
        })
        return returnType
      }).filter((type: InitiativeType) => {
        let hasTacticsInDateRange = false
        type.initiatives.forEach((initiative: Initiative) => {
          if(this.isPlanningItemTouchingMonth(initiative)){
            hasTacticsInDateRange = true
          }
        })
        return hasTacticsInDateRange
      })
    },
    totalCategorizedInitiatives(): number {
      return this.categorizedInitiatives.length
    },
    selectedTags: {
      get(): Tag[] {
        if (this.localSelectedTags.length) {
          return this.$store.getters.selectedTags
        }
        return []
      },
      set(newValue: Tag[]) {
        // If "no tag" filter was active, clear it
        if (newValue.length > 0 && this.$store.getters.filterNoTag) {
          this.$store.commit('filterNoTag', false)
          window.localStorage.removeItem(this.currentNoTagFilterKey)
        }
        this.localSelectedTags = newValue
        if (newValue.length == 0) {
          this.$store.commit(
            'updateSelectedTags',
            this.$store.getters.currentTags
          )
          window.localStorage.removeItem(this.currentTagsFilterKey)
          this.$store.commit('isTagFilterActive', false)
        } else {
          this.$store.commit('updateSelectedTags', newValue)
          window.localStorage.setItem(
            this.currentTagsFilterKey,
            this.$store.getters.selectedTagsQueryString
          )
          this.$store.commit('isTagFilterActive', true)
        }
        this.manageSubscriptionFilters()
      },
    },
    filterNoTag: {
      get(): boolean {
        return this.$store.getters.filterNoTag
      },
      set(newValue: boolean) {
        this.$store.commit('filterNoTag', newValue)
        if (newValue) {
          this.localSelectedTags = []
          this.$store.commit('updateSelectedTags', [])
          this.$store.commit('isTagFilterActive', true)
          window.localStorage.setItem(this.currentNoTagFilterKey, 'true')
          window.localStorage.removeItem(this.currentTagsFilterKey)
        } else {
          window.localStorage.removeItem(this.currentNoTagFilterKey)
          if (this.localSelectedTags.length === 0) {
            this.$store.commit('isTagFilterActive', false)
          }
        }
        this.manageSubscriptionFilters()
      },
    },
    selectedSubscriptions: {
      get(): Subscription[] {
        if (this.localSelectedSubscriptions.length) {
          return this.$store.getters.selectedSubscriptions
        }
        return []
      },
      set(newValue: Subscription[]) {
        this.localSelectedSubscriptions = newValue
        this.manageSubscriptionFilters()
      },
    },

    accordionActiveIndicies(): number[] {
      const returnArray = [] as number[]
      this.$store.getters.currentDateRange //Hack to force computed property update when date range changes
      if (
        window.localStorage.getItem(
          'tf-tactic-types-' + this.$store.getters.currentPlanIntID.toString()
        ) != 'false' &&
        this.$store.getters.currentTacticTypes.length &&
        !this.isInitiativesViewDisplayed
      ) {
        returnArray.push(0)
      }
      if (
        window.localStorage.getItem(
          'tf-initiatives-' + this.$store.getters.currentPlanIntID.toString()
        ) != 'false' &&
        this.totalCategorizedInitiatives
      ) {
        returnArray.push(1)
      }
      if (
        window.localStorage.getItem(
          'tf-strategy-' + this.$store.getters.currentPlanIntID.toString()
        ) != 'false' &&
        this.$store.getters.currentTags.length
      ) {
        returnArray.push(2)
      }
      if (
        window.localStorage.getItem(
          'tf-subscriptions-' + this.$store.getters.currentPlanIntID.toString()
        ) != 'false' &&
        this.$store.getters.currentSubscriptions.length
      ) {
        returnArray.push(3)
      }
      return returnArray
    },

    shouldShowCalendarsOfInterest(): boolean {
      return this.$route.name == 'Table' ? false : true
    },
    // Consolidated filter state for debounced watcher
    filterState(): object {
      return {
        tacticTypes: this.localSelectedTacticTypes,
        initiatives: this.localSelectedInitiatives,
        customizations: this.localSelectedInitiativeCustomizations,
        filterNoInitiative: this.filterNoInitiative,
        tags: this.localSelectedTags,
        filterNoTag: this.filterNoTag,
        subscriptions: this.localSelectedSubscriptions,
        nestedPlans: this.visibleNestedPlanIntIds,
        leadPlanVisible: this.isLeadPlanVisible,
      }
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler(route) {
        this.currentRoutePath = route.fullPath
      },
    },
    localShowHide() {
      this.updateFiltersFromGlobalStore()
    },
    // Consolidated watcher with debouncing - replaces 7 separate watchers
    filterState: {
      deep: true,
      handler() {
        if (this.updateFiltersCountTimer) {
          clearTimeout(this.updateFiltersCountTimer)
        }
        this.updateFiltersCountTimer = setTimeout(() => {
          this.$store.dispatch('updateActiveFiltersCount')
          this.updateFiltersCountTimer = null
        }, 100)
      },
    },
  },
  methods: {
    getSelectedTypesForChannel(channel: Channel): TacticType[] {
      const typeIdsInChannel = channel.tacticTypes.map((type) => type.id.intID)
      return this.selectedTacticTypes.filter((type) => typeIdsInChannel.includes(type.id.intID))
    },
    isPlanningItemTouchingMonth(planningItem: PlanningItem): boolean {
      if (
        planningItem.endDate < this.$store.getters.currentDateRange[0] ||
        planningItem.startDate > this.$store.getters.currentDateRange[1]
      ) {
        return false
      }
      return true
    },
    isPlanningItemInFilteredOutPlan(planningItem: PlanningItem): boolean {
      if (planningItem.isLead && !this.$store.getters.isLeadPlanVisible) {
        return true
      }
      if (
        planningItem.isNested &&
        !this.$store.getters.visibleNestedPlansIntIds?.find(
          (planIntId) => planIntId === planningItem.planId.intID
        )
      ) {
        return true
      }
      return false
    },
    onCheckboxChange($event) {
      $event.preventDefault()
      $event.stopPropagation()
    },
    onChannelCheckboxChange($event, channel: Channel) {
      this.onCheckboxChange($event)

      const selectedTypeIntIds = this.selectedTacticTypes.map((type) => type.id.intID)
      const channelTypeIntIds = channel.tacticTypes.map((type) => type.id.intID)
      if(channel.tacticTypes.filter((type) => selectedTypeIntIds.includes(type.id.intID)).length){
        this.selectedTacticTypes = this.selectedTacticTypes.filter((type) => !channelTypeIntIds.includes(type.id.intID))
      }else{
        this.selectedTacticTypes = this.selectedTacticTypes.concat(channel.tacticTypes)
      }
    },
    onInitiativeTypeCheckboxChange($event, type: InitiativeType) {
      this.onCheckboxChange($event)

      const visibleInitiatives = type.initiatives.filter((init: Initiative) => this.isPlanningItemTouchingMonth(init))
      const selectedInitiativeIntIds = this.selectedInitiatives.map((init: Initiative) => init.id.intID)
      const typeInitiativeIntIds = visibleInitiatives.map((init: Initiative) => init.id.intID)

      if (visibleInitiatives.filter((init: Initiative) => selectedInitiativeIntIds.includes(init.id.intID)).length) {
        this.selectedInitiatives = this.selectedInitiatives.filter((init: Initiative) => !typeInitiativeIntIds.includes(init.id.intID))
      } else {
        this.selectedInitiatives = this.selectedInitiatives.concat(visibleInitiatives)
      }
    },
    getVisibleInitiativesForType(type: InitiativeType): Initiative[] {
      return type.initiatives.filter((init: Initiative) => this.isPlanningItemTouchingMonth(init))
    },
    getVisibleInitiativesCountForType(type: InitiativeType): number {
      let count = 0
      type.initiatives.forEach((initiative: Initiative) => {
        if (this.isPlanningItemTouchingMonth(initiative)) {
          count++
          initiative.initiativeCustomizations?.forEach((customization: InitiativeCustomization) => {
            if (
              !this.isPlanningItemInFilteredOutPlan(customization) &&
              this.isPlanningItemTouchingMonth(customization) &&
              (this.isLeadPlan || customization.planId.intID === this.currentPlan.id.intID)
            ) {
              count++
            }
          })
        }
      })
      return count
    },
    getSelectedInitiativesCountForType(type: InitiativeType): number {
      const selectedInitiativeIntIds = this.selectedInitiatives.map((init: Initiative) => init.id.intID)
      const selectedCustomizationIntIds = this.selectedInitiativeCustomizations.map((c: InitiativeCustomization) => c.id.intID)
      let count = 0
      type.initiatives.forEach((initiative: Initiative) => {
        if (this.isPlanningItemTouchingMonth(initiative)) {
          if (selectedInitiativeIntIds.includes(initiative.id.intID)) {
            count++
          }
          initiative.initiativeCustomizations?.forEach((customization: InitiativeCustomization) => {
            if (
              !this.isPlanningItemInFilteredOutPlan(customization) &&
              this.isPlanningItemTouchingMonth(customization) &&
              (this.isLeadPlan || customization.planId.intID === this.currentPlan.id.intID) &&
              selectedCustomizationIntIds.includes(customization.id.intID)
            ) {
              count++
            }
          })
        }
      })
      return count
    },
    getSelectedInitiativesForType(type: InitiativeType): Initiative[] {
      const visibleInitiatives = this.getVisibleInitiativesForType(type)
      const selectedInitiativeIntIds = this.selectedInitiatives.map((init: Initiative) => init.id.intID)
      return visibleInitiatives.filter((init: Initiative) => selectedInitiativeIntIds.includes(init.id.intID))
    },
    isCustomViewForCurrentPlan(customView: CustomView) {
      return customView.viewConfig
        .slice(0, customView.viewConfig.indexOf('?'))
        .indexOf(this.$store.getters.currentPlanIntID.toString()) > -1
        ? true
        : false
    },
    updateFiltersFromGlobalStore() {
      if (this.$store.getters.isChannelFilterActive) {
        this.localSelectedTacticTypes = this.$store.getters.selectedTacticTypes
      }
      if (this.$store.getters.isInitiativeFilterActive) {
        this.localSelectedInitiatives = this.$store.getters.selectedInitiatives
        this.localSelectedInitiativeCustomizations = this.$store.getters.selectedInitiativeCustomizations
      }
      if (this.$store.getters.isTagFilterActive) {
        this.localSelectedTags = this.$store.getters.selectedTags
      }
      if (this.$store.getters.isSubscriptionFilterActive) {
        this.localSelectedSubscriptions = this.$store.getters.selectedSubscriptions
      }
    },
    onClearFiltersClick() {
      this.isLeadPlanVisible = true
      this.visibleNestedPlanIntIds = this.$store.getters.currentPlan.plans.length ? this.currentNestedPlans.map((plan) => plan.id.intID) : [this.$store.getters.currentPlan.id.intID]
      this.selectedTacticTypes = []
      this.selectedInitiatives = []
      this.selectedInitiativeCustomizations = []
      this.filterNoInitiative = false
      this.selectedTags = []
      this.filterNoTag = false
      this.selectedSubscriptions = []
    },
    manageSubscriptionFilters() {
      if (
        this.$store.getters.isChannelFilterActive == true ||
        this.$store.getters.isInitiativeFilterActive == true ||
        this.$store.getters.isTagFilterActive == true
      ) {
        //Non-subscription filters are active
        if (this.$store.getters.isSubscriptionFilterActive == true) {
          this.setSubscriptionFilters(this.localSelectedSubscriptions)
        } else {
          this.setSubscriptionFilters([])
        }
      } else {
        //Non-subscription filters are NOT active
        if (this.$store.getters.isSubscriptionFilterActive == true) {
          if (this.localSelectedSubscriptions.length == 0) {
            this.clearSubscriptionFilters()
          } else {
            this.setSubscriptionFilters(this.localSelectedSubscriptions)
          }
        } else {
          this.setSubscriptionFilters(this.localSelectedSubscriptions)
        }
      }
    },
    clearSubscriptionFilters() {
      this.$store.commit('updateSelectedSubscriptions', [])
      window.localStorage.removeItem(this.currentSubscriptionsFilterKey)
      this.$store.commit('isSubscriptionFilterActive', false)
    },
    setSubscriptionFilters(newValue: Subscription[]) {
      this.$store.commit('updateSelectedSubscriptions', newValue)
      window.localStorage.setItem(
        this.currentSubscriptionsFilterKey,
        this.$store.getters.selectedSubscriptionsQueryString
      )
      this.$store.commit('isSubscriptionFilterActive', true)
    },
    getAccordionTabNameForIndex(index: number): string {
      // TODO: update indicies if lead or nested plan
      switch (index) {
        case 0:
          return 'channels'
        case 1:
          return Channel.CHANNEL_NAME_INITIATIVES.toLowerCase()
        case 2:
          return 'strategy'
        case 3:
          return 'subscriptions'
        default:
          return ''
      }
    },
    onAccordionTabOpen(event) {
      window.localStorage.setItem(
        'tf-' +
          this.getAccordionTabNameForIndex(event.index) +
          '-' +
          this.$store.getters.currentPlanIntID.toString(),
        'true'
      )
    },
    onAccordionTabClose(event) {
      window.localStorage.setItem(
        'tf-' +
          this.getAccordionTabNameForIndex(event.index) +
          '-' +
          this.$store.getters.currentPlanIntID.toString(),
        'false'
      )
    },
    areAllChannelsSelected(): boolean {
      const allTacticTypeIntIds = this.currentChannelsSorted.flatMap((channel: Channel) =>
        channel.tacticTypes.map((type: TacticType) => type.id.intID)
      )
      const selectedTypeIntIds = this.selectedTacticTypes.map((type: TacticType) => type.id.intID)
      return allTacticTypeIntIds.every((typeIntId: number) => selectedTypeIntIds.includes(typeIntId))
    },
    getChannelsToggleLabel(): string {
      return this.areAllChannelsSelected() ? 'Deselect All' : 'Select All'
    },
    onToggleAllChannels() {
      if (this.areAllChannelsSelected()) {
        // Deselect all channels (clear all tactic types)
        this.selectedTacticTypes = []
      } else {
        // Select all channels (add all tactic types from all channels)
        const allTacticTypes = this.currentChannelsSorted.flatMap((channel: Channel) => channel.tacticTypes)
        this.selectedTacticTypes = allTacticTypes
      }
    },
    areAllTagsInCategorySelected(tagCategory: TagCategory): boolean {
      const selectedTagIntIds = this.selectedTags.map((tag: Tag) => tag.id.intID)
      return tagCategory.tags.every((tag: Tag) => selectedTagIntIds.includes(tag.id.intID))
    },
    getTagCategoryToggleLabel(tagCategory: TagCategory): string {
      return this.areAllTagsInCategorySelected(tagCategory) ? 'Deselect All' : 'Select All'
    },
    onToggleAllTagsInCategory(tagCategory: TagCategory) {
      const categoryTagIntIds = tagCategory.tags.map((tag: Tag) => tag.id.intID)
      if (this.areAllTagsInCategorySelected(tagCategory)) {
        // Deselect all tags in this category
        this.selectedTags = this.selectedTags.filter((tag: Tag) => !categoryTagIntIds.includes(tag.id.intID))
      } else {
        // Select all tags in this category (add ones not already selected)
        const currentSelectedTagIntIds = this.selectedTags.map((tag: Tag) => tag.id.intID)
        const tagsToAdd = tagCategory.tags.filter((tag: Tag) => !currentSelectedTagIntIds.includes(tag.id.intID))
        this.selectedTags = [...this.selectedTags, ...tagsToAdd]
      }
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.tactic-filters-wrapper {
  max-width: 28rem;

  .p-sidebar-close {
    display: none;
  }

  .tactic-filters-header {
    padding: 1.6rem 1.6rem 1.6rem 1rem;
    margin-bottom: 2rem;

    h2 {
      margin-bottom: 0;
    }
  }

  .p-panel {
    border-top: 0.2rem solid $white-smoke;

    .p-panel-header,
    .p-panel-content {
      border: 0;
      background-color: transparent;
    }
    .p-panel-header {
      padding: 1.2rem 1rem;
    }
    .p-panel-title {
      font-size: 1.4rem;
      line-height: 1.6rem;
    }
  }
  label{
    &.is-lead,
    &.is-nested-variant {
      display: flex;

      &::before {
        content: '\e925'; // pi-home
        display: inline-block;
        padding-right: 0.6rem;
        font-family: 'primeicons';
        font-size: 1.2rem;
        line-height: 1.8rem;
        font-style: normal;
        font-weight: normal;
        font-variant: normal;
        text-transform: none;
      }
    }
    &.is-nested-variant {
      &::before {
        content: '\e9c1'; // pi-link
      }
    }
  }

  .initiatives-accordion {
    padding-left: 1rem;
  }

  .p-field-checkbox {
    align-items: flex-start;
  }

  .channel-accordion-tab,
  .initiative-type-accordion-tab,
  .initiative-accordion-tab {
    margin-bottom: 1rem;

    .p-accordion-header {
      .p-accordion-header-link {
        padding: 0;
        border: 0;
        background-color: #fff !important;
        font-size: 1.1rem;
      }
      &.p-highlight {
        .p-checkbox {
          .p-checkbox-box {
            border-color: #000;
          }
        }
      }
    }
    .p-accordion-content {
      padding-bottom: 0;

      .p-field-checkbox {
        margin-bottom: 1rem;

        &:last-of-type {
          margin-bottom: 0;
        }
      }
    }
    .p-field-checkbox {
      margin-bottom: 0;
    }

    .channel-selected-types-count,
    .initiative-type-selected-count {
      margin-left: 1rem;
    }
  }

  .initiative-accordion-tab {
    &:not(:has(.nested-variant-item)) {
      .p-accordion-header-link {
        cursor: default;
      }
      .p-checkbox {
        cursor: pointer;
      }
      .p-accordion-toggle-icon {
        visibility: hidden;
        pointer-events: none;
      }
      .p-toggleable-content {
        display: none;
      }
    }
  }

  .initiative-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  }
  .nested-variant-item {
    padding-left: 1rem;
  }
}

.p-sidebar.p-sidebar-active {
  overflow-y: scroll;
}

.tactic-filters-wrapper {
  .p-accordion-header {
    .p-accordion-header-link {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      padding: 0.8rem 1.6rem 0.8rem 0.8rem;
      border-color: #f9f9f9 !important;
      background-color: #f9f9f9 !important;
    }
    .p-accordion-header-link,
    .p-accordion-header-text {
      font-size: 1.4rem;
      line-height: 1.75rem;
    }

    &.p-disabled {
      .p-accordion-toggle-icon {
        visibility: hidden;
        pointer-events: none;
      }
    }
  }
  .p-accordion-content {
    border-color: white;
  }
  .p-inputswitch {
    transform: scale(0.75);

    &.p-inputswitch-checked {
      .p-inputswitch-slider {
        background-color: $tacticColorPR;
      }
    }
    &:not(.p-disabled):hover {
      .p-inputswitch-slider {
        background-color: $tacticColorPRBkg;
      }
    }
  }
}
</style>